import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Site, SiteStatus } from '@core/generated/gql-types';
import { useSiteStore } from '@core/site/useSiteStore';
import { generateFriendlyAddress } from '@core/util/google-places.service';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';

export interface ClientSiteSelectorProps {
  clientId: string;
  value: string;
  onChange: (siteId: string) => void;
  onSiteSelected?: (site: Site | null) => void;
  onActiveSitesChange?: (sites: Site[]) => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
}

const ClientSiteSelector: React.FC<ClientSiteSelectorProps> = ({
  clientId,
  value,
  onChange,
  onSiteSelected,
  onActiveSitesChange,
  error,
  touched,
  disabled,
}) => {
  const { getTerm } = useTemplateStore();
  const { fetchSitesForClient, isLoading, sites } = useSiteStore();

  useEffect(() => {
    if (!clientId) return;
    fetchSitesForClient(clientId);
  }, [clientId, fetchSitesForClient]);

  const activeSites = useMemo(() => {
    return sites
      .filter(
        (site) =>
          site.clientId === clientId && site.status === SiteStatus.Active,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sites, clientId]);

  useEffect(() => {
    onActiveSitesChange?.(activeSites);
  }, [activeSites, onActiveSitesChange]);

  useEffect(() => {
    if (activeSites.length === 1 && !value) {
      const onlySite = activeSites[0];
      onChange(onlySite.id);
      onSiteSelected?.(onlySite);
    }
  }, [activeSites, value, onChange, onSiteSelected]);

  const selectedSite = activeSites.find((site) => site.id === value) ?? null;

  const showDropdown = activeSites.length > 1;

  if (!clientId) {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium">Select Site</label>
        <button
          type="button"
          disabled
          className="relative w-full cursor-not-allowed rounded-lg border bg-gray-50 py-2 pr-10 pl-3 text-left text-gray-400 shadow-xs"
        >
          <span className="block truncate">
            Select a {getTerm(TemplateTerm.CLIENT, false).toLowerCase()} first
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-300"
              aria-hidden="true"
            />
          </span>
        </button>
      </div>
    );
  }

  if (isLoading && activeSites.length === 0) {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium"> Select Site</label>
        <p className="text-sm text-gray-500">Loading sites…</p>
      </div>
    );
  }

  if (activeSites.length === 0) {
    return (
      <div>
        <label className="mb-1 block text-sm font-medium">Select Site</label>
        <p className="text-sm text-red-600">
          No sites for this {getTerm(TemplateTerm.CLIENT, false).toLowerCase()}.
          Add a{' '}
          <Link
            to={`/clients/${clientId}`}
            className="font-medium text-indigo-600 hover:text-indigo-700"
          >
            site
          </Link>{' '}
          before creating a job.
        </p>
        {touched && error ? (
          <div className="mt-1 text-xs text-red-500">{error}</div>
        ) : null}
      </div>
    );
  }

  if (!showDropdown) {
    const site = activeSites[0];
    const addressLabel = generateFriendlyAddress(site.address ?? {});
    return (
      <div>
        <label className="mb-1 block text-sm font-medium">Select Site</label>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <p className="font-medium text-gray-900">{site.name}</p>
          {addressLabel ? (
            <p className="text-gray-600">{addressLabel}</p>
          ) : null}
        </div>
        {touched && error ? (
          <div className="mt-1 text-xs text-red-500">{error}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <label className="mb-1 block text-sm font-medium">Select Site</label>
      <Listbox
        value={value}
        onChange={(siteId: string) => {
          onChange(siteId);
          const site =
            activeSites.find((activeSite) => activeSite.id === siteId) ?? null;
          onSiteSelected?.(site);
        }}
        disabled={disabled}
      >
        <div className="relative">
          <Listbox.Button className="relative w-full cursor-default rounded-lg border bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:outline-hidden focus-visible:border-blue-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 disabled:bg-gray-50">
            <span className="block truncate">
              {selectedSite?.name || 'Select site'}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>
          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm">
              {activeSites.map((site) => {
                const addressLabel = generateFriendlyAddress(
                  site.address ?? {},
                );
                return (
                  <Listbox.Option
                    key={site.id}
                    className={({ active }) =>
                      `relative cursor-default py-2 pr-4 pl-10 select-none ${
                        active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                      }`
                    }
                    value={site.id}
                  >
                    {({ selected }) => (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          }`}
                        >
                          {site.name}
                        </span>
                        {addressLabel ? (
                          <span className="block truncate text-xs text-gray-500">
                            {addressLabel}
                          </span>
                        ) : null}
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                            <FontAwesomeIcon icon={faCheck} />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                );
              })}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {selectedSite ? (
        <p className="mt-2 text-sm text-gray-600">
          {generateFriendlyAddress(selectedSite.address ?? {}) ||
            'No address on file for this site'}
        </p>
      ) : null}
      {touched && error ? (
        <div className="mt-1 text-xs text-red-500">{error}</div>
      ) : null}
    </div>
  );
};

export default ClientSiteSelector;
